/**
 * Unified Calendar Filter Component
 *
 * Main filter component that displays calendars grouped by source
 * and family labels in a unified list. Mirrors Google Calendar's
 * filtering approach.
 */

import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { FamilyMember } from '@/types/family-member';
import type { FilterItem } from '@/types/calendar-filter';
import { CalendarFilterManager } from '@/lib/managers/CalendarFilterManager';
import { FilterSectionHeader } from './FilterSectionHeader';
import { FilterItemRow } from './FilterItemRow';

type UnifiedCalendarFilterProps = {
  calendars: DeviceCalendar[];
  familyMembers: FamilyMember[];
  selectedCalendarIds: string[];
  selectedLabelIds: string[];
  onCalendarChange: (ids: string[]) => void;
  onLabelChange: (ids: string[]) => void;
  showLabels?: boolean;
};

export function UnifiedCalendarFilter({
  calendars,
  familyMembers,
  selectedCalendarIds,
  selectedLabelIds,
  onCalendarChange,
  onLabelChange,
  showLabels = true,
}: UnifiedCalendarFilterProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  // Build filter sections
  const membersToShow = showLabels ? familyMembers : [];
  const sections = CalendarFilterManager.buildFilterSections(
    calendars,
    membersToShow,
    selectedCalendarIds,
    selectedLabelIds
  );

  const handleItemToggle = (item: FilterItem) => {
    const result = CalendarFilterManager.toggleItemSelection(
      item,
      selectedCalendarIds,
      selectedLabelIds,
      calendars
    );

    if (item.type === 'calendar') {
      onCalendarChange(result.calendarIds);
    } else {
      onLabelChange(result.labelIds);
    }
  };

  const handleSectionToggle = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    const result = CalendarFilterManager.toggleSectionSelection(
      section,
      selectedCalendarIds,
      selectedLabelIds
    );

    // Check if this section contains calendars or labels
    const hasCalendars = section.items.some(item => item.type === 'calendar');
    const hasLabels = section.items.some(item => item.type === 'label');

    if (hasCalendars) {
      // Ensure at least one calendar remains selected
      if (result.calendarIds.length === 0) {
        const primary = calendars.find(c => c.isPrimary);
        result.calendarIds = primary ? [primary.id] : [calendars[0]?.id].filter(Boolean);
      }
      onCalendarChange(result.calendarIds);
    }

    if (hasLabels) {
      onLabelChange(result.labelIds);
    }
  };

  if (calendars.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: palette.background }]}>
        <Text style={[styles.emptyText, { color: palette.text }]}>
          No calendars found
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: palette.background }]}>
      {sections.map(section => {
        const selectedCount = section.items.filter(item => item.isSelected).length;

        return (
          <View key={section.id}>
            <FilterSectionHeader
              title={section.title}
              itemCount={section.items.length}
              selectedCount={selectedCount}
              onToggleAll={() => handleSectionToggle(section.id)}
            />
            {section.items.map(item => (
              <FilterItemRow
                key={item.id}
                item={item}
                onToggle={handleItemToggle}
              />
            ))}
          </View>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyText: {
    padding: Spacing.xxl,
    textAlign: 'center',
    opacity: 0.6,
    fontFamily: getBodyFont(),
  },
});
